<template>
  <div
    class="inplace-edit"
    @click="startEditing"
    v-if="!isEditing"
    :class="{ 'inplace-edit--disabled': disabled, 'inplace-edit--empty': !displayValue }"
  >
    <slot name="display">
      <span>{{ displayValue || placeholder }}</span>
    </slot>
  </div>
  <div v-else class="inplace-edit-form">
    <slot
      name="editor"
      :value="editValue"
      :update="updateValue"
      :save="saveChanges"
      :cancel="cancelEditing"
    >
      <!-- Default fallback to basic input/textarea -->
      <textarea
        v-if="multiline"
        v-model="editValue"
        @blur="saveChanges"
        @keydown.enter.prevent="saveChanges"
        @keydown.esc="cancelEditing"
        ref="input"
      ></textarea>
      <input
        v-else
        type="text"
        v-model="editValue"
        @blur="saveChanges"
        @keydown.enter="saveChanges"
        @keydown.esc="cancelEditing"
        ref="input"
      />
    </slot>
  </div>
</template>

<script>
  import { defineComponent, ref, watch, computed, onMounted } from 'vue';

  export default defineComponent({
    name: 'InplaceEdit',

    props: {
      modelValue: {
        type: [String, Number, Array],
        default: '',
      },
      // Legacy support
      value: {
        type: [String, Number, Array],
        default: undefined,
      },
      label: {
        type: String,
        default: null,
      },
      placeholder: {
        type: String,
        default: 'Klik om te bewerken',
      },
      multiline: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      autoFocus: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['update:modelValue', 'update'],

    setup(props, { emit }) {
      const isEditing = ref(false);
      const input = ref(null);

      // Prefer explicit `value` (legacy); modelValue defaults to '' so it is never undefined.
      const currentValue = computed(() => {
        return props.value !== undefined ? props.value : props.modelValue;
      });

      const editValue = ref(currentValue.value);

      watch(
        currentValue,
        (newValue) => {
          if (!isEditing.value) {
            editValue.value = newValue;
          }
        },
        { immediate: true }
      );

      // Auto-focus on mount if prop is set
      onMounted(() => {
        if (props.autoFocus && !props.disabled) {
          startEditing();
        }
      });

      const startEditing = () => {
        if (props.disabled) return;
        isEditing.value = true;
        editValue.value = currentValue.value;
        setTimeout(() => {
          input.value?.focus();
          input.value?.select();
        });
      };

      const updateValue = (value) => {
        editValue.value = value;
      };

      const saveChanges = () => {
        isEditing.value = false;
        if (editValue.value !== currentValue.value) {
          emit('update:modelValue', editValue.value);
          emit('update', editValue.value); // Legacy support
        }
      };

      const cancelEditing = () => {
        isEditing.value = false;
        editValue.value = currentValue.value;
      };

      // Display value (label takes precedence) - make it computed for reactivity
      const displayValue = computed(() => {
        return props.label || currentValue.value;
      });

      return {
        isEditing,
        editValue,
        input,
        startEditing,
        saveChanges,
        cancelEditing,
        updateValue,
        displayValue,
      };
    },
  });
</script>

<style scoped lang="scss">
  .inplace-edit {
    padding: 0.5rem 0.5rem 0.5rem 0;
    cursor: pointer;

    &:hover {
      background: var(--slate-100);
      border-radius: 0.25rem;
    }

    &--disabled {
      cursor: default;
      opacity: 1;
      pointer-events: none;
    }

    &--empty {
      color: var(--slate-400);
      font-style: italic;
    }
  }

  .inplace-edit-form {
    input,
    textarea {
      width: 100%;
      padding: 0.5rem;
      border: 1px solid var(--slate-100);
      border-radius: 0.25rem;

      &:focus {
        outline: none;
        border-color: var(--primary-100);
      }
    }

    textarea {
      min-height: 100px;
      resize: vertical;
    }
  }
</style>
